/**
 * ViewBuilder — the view CONFIGURATOR's UI: a HUD pill opening an anchored
 * Popover panel of concern sections. It reconfigures the current view LIVE so
 * you can reason on the board right now; saving is optional. Two tiers:
 *
 *   - Reconfigure: a real edit projects the config onto the board immediately —
 *     no Apply click. Per-tab and persisted, so a config in one tab never leaks
 *     to another and survives reloads;
 *   - Save: the config's clause lines land in `.arch` text via the mutation
 *     service — "Update view" (modify in place) or "Save as view" (create).
 *
 * All state + persistence live in useViewDrafts; this file is view-only. The
 * per-clause-kind editors live in the sibling RowEditor module.
 */
import { useState, type ReactElement, type ReactNode } from "react";
import { Wand2, Plus, X, ArrowUp, ArrowDown } from "lucide-react";
import { Button, IconButton, Tooltip, useContextMenu, type ContextMenuItem } from "@archlang/ui";
import { Popover, PopoverTrigger, PopoverPanel } from "../components/Popover.js";
import { HudChip } from "../components/HudChip.js";
import { useViewDrafts } from "./useViewDrafts.js";
import { findView } from "./viewRepr.js";
import { summarizeDraft } from "./viewSummary.js";
import { useSelectorVocabulary, type SelectorVocabulary } from "./useSelectorVocabulary.js";
import { rowText, seedRow, isKnobRowText, type ClauseRow, type RowKind } from "./clauseModel.js";
import { PlaneSelect } from "../components/pickers/PlaneSelect.js";
import { ColorBySelect } from "../components/pickers/ColorBySelect.js";
import { RowEditor } from "./RowEditor.js";
import { useActiveView } from "../workbench/ActiveViewContext.js";
import { useWorkspaceModel } from "../workbench/WorkspaceModelContext.js";
import { useEffectiveViewName } from "./useEffectiveViewName.js";

export interface ViewBuilderProps {
  /**
   * Offer the SAVE tier — writing the config back into `.arch` text.
   *
   * False leaves the reconfigure tier intact and drops everything that only
   * serves a save: the name field, the collision warning, the save button. A
   * host that cannot write files still gets the reasoning tool, rather than a
   * button that fails when pressed.
   */
  readonly canSave?: boolean;
}

// Hoisted: an object literal as a parameter default is a FRESH object per
// call, which churns every memo/effect dep that reads it. Same rule the board's
// gesture-perf collapse was traced to.
const NO_PROPS: ViewBuilderProps = {};

export function ViewBuilder({ canSave = true }: ViewBuilderProps = NO_PROPS): ReactNode {
  const [open, setOpen] = useState(false);
  const drafts = useViewDrafts();
  const { draftView } = useActiveView();
  // The active view usually lives in a view TAB, not the raw ActiveView
  // context (which the HUD picker sets) — read the effective name so the
  // seed fires on a view tab too.
  const activeViewName = useEffectiveViewName();
  const { resolvedModel } = useWorkspaceModel();

  const handleOpenChange = (next: boolean): void => {
    // Opening onto another view's board with an untouched draft → prepopulate
    // from that view so you tweak it into a variant.
    if (next && !open && !draftView && drafts.pristine && activeViewName) {
      // findView = qualified-FIRST then bare fallback — the board's canonical
      // precedence. A single OR-predicate find let a first-in-source scoped
      // twin hijack the seed, so the builder edited a view the board wasn't
      // rendering (r35).
      const view = findView(resolvedModel?.views, activeViewName);
      if (view) drafts.seedFromView(view);
    }
    setOpen(next);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange} placement="bottom-end">
      <PopoverTrigger>
        {({ ref, props }) => (
          <Tooltip content="Reconfigure the view live — show / hide / group / style. Reason on the board; save when you want.">
            <HudChip ref={ref} {...props} active={!!draftView} accent="sky">
              <Wand2 width={13} height={13} aria-hidden="true" className="shrink-0" />
              <span>{draftView ? `Configuring: ${draftView.name}` : "Build view"}</span>
              <span className="opacity-60">▾</span>
            </HudChip>
          </Tooltip>
        )}
      </PopoverTrigger>
      <PopoverPanel className="z-overlay w-96 max-h-[70vh] space-y-2 overflow-auto rounded-lg border border-slate-700 bg-slate-900/(--tint-solid) p-2.5 shadow-xl backdrop-blur-md outline-none">
        <BuilderPanel drafts={drafts} canSave={canSave} onClose={() => setOpen(false)} />
      </PopoverPanel>
    </Popover>
  );
}

// The composer's concern sections. Contents is the only ordered one (hide-
// after-show), so reordering lives there and nowhere else.
//
// The builder composes STRUCTURE views (VIEWS.md §6): a set of elements drawn as
// a board / table / matrix / grid — its representations reshape the same `show`
// set. PROCESS views (`flow <Process>`) are authored on the process itself, so
// there is no Structure/Process switch here; a process view opened in the
// builder still shows its flow editor (kept editable), but the builder never
// creates one.
const CONTENT_KINDS = ["show", "hide", "focus"] as const;
// All representation clauses (any one makes a view non-board); used to enforce
// exactly-one representation — and to clear a stray `flow` when a structure
// representation is picked.
const REP_KINDS = ["flow", "table", "matrix", "grid", "index"] as const;
// The structure representations offered here (flow is a process view, separate).
const STRUCTURE_KINDS = ["table", "matrix", "grid", "index"] as const;
type ContentKind = (typeof CONTENT_KINDS)[number];
type RepChoice = "board" | (typeof REP_KINDS)[number];
const STRUCTURE_CHOICES: readonly RepChoice[] = ["board", "table", "matrix", "grid", "index"];
const REP_LABELS: Record<RepChoice, string> = {
  board: "Board",
  flow: "Flow",
  table: "Table",
  matrix: "Matrix",
  grid: "Grid",
  index: "Cards",
};

function Section({ title, children }: { title: string; children: ReactNode }): ReactElement {
  return (
    <section className="space-y-1.5 border-t border-slate-800 pt-2">
      <h3 className="text-[10px] font-medium uppercase tracking-wide text-slate-500">{title}</h3>
      {children}
    </section>
  );
}

export interface ViewClauseComposerProps {
  /** The clause rows being composed. */
  readonly rows: readonly ClauseRow[];
  /** Replace the whole row list — every section mutation funnels here so the
   *  backing (drafts → .arch, or a doc ```view block) owns normalisation +
   *  persistence. */
  readonly onChangeRows: (rows: readonly ClauseRow[]) => void;
  /** Live compile errors to surface under the sections (empty = valid). */
  readonly errors: readonly string[];
  /** Selector vocabulary (types / elements / aspect keys / planes / processes)
   *  from the host's compiled model — passed in so the composer stays context-
   *  agnostic and works in the HUD AND inside a doc embed's own providers. */
  readonly vocab: SelectorVocabulary;
}

/**
 * ViewClauseComposer — the view WYSIWYG composer's BODY: the concern sections
 * (Contents / Grouping / Style / Representation / Knobs / Advanced) over a clause-row
 * list. Pure + host-agnostic — it takes `rows` + a single `onChangeRows` sink +
 * the selector vocabulary, and owns NO name field, persistence, or chrome (the
 * NAME is host-specific — locked-while-modifying in the HUD, a plain field in a
 * doc block — so each host renders it around the composer). The SAME composer
 * backs the HUD configurator and the in-doc ```view builder.
 */
export function ViewClauseComposer({
  rows,
  onChangeRows,
  errors,
  vocab,
}: ViewClauseComposerProps): ReactElement {
  const [rawOpen, setRawOpen] = useState(false);
  const [knobsOpen, setKnobsOpen] = useState(false);
  const menu = useContextMenu();
  const summary = summarizeDraft(rows);

  // Row mutations — pure array ops funnelled through the single sink.
  const setRow = (index: number, row: ClauseRow): void =>
    onChangeRows(rows.map((r, i) => (i === index ? row : r)));
  const addRow = (row: ClauseRow): void => onChangeRows([...rows, row]);
  const removeRow = (index: number): void => onChangeRows(rows.filter((_, i) => i !== index));
  const replaceRows = (next: readonly ClauseRow[]): void => onChangeRows(next);

  // Right-click menu for a clause/rule/column row: copy its written text plus
  // an in-place remove (danger, grouped below a separator). An empty label
  // still copies "" harmlessly; remove routes through the same handler as the ✕.
  const rowMenu = (index: number, label: string): ContextMenuItem[] => [
    { label: "Copy name", onClick: () => void navigator.clipboard?.writeText(label) },
    { label: "Remove", danger: true, separator: true, onClick: () => removeRow(index) },
  ];

  // --- section composer primitives over the row list ---
  const entriesOf = (pred: (r: ClauseRow) => boolean): { row: ClauseRow; index: number }[] =>
    rows.map((row, index) => ({ row, index })).filter((e) => pred(e.row));

  // A single-slot concern (group / on / colour): set or clear it in place.
  const setSingle = (kind: RowKind, row: ClauseRow | null): void => {
    const i = rows.findIndex((r) => r.kind === kind);
    const next = [...rows];
    if (!row) {
      if (i >= 0) next.splice(i, 1);
    } else if (i >= 0) next[i] = row;
    else next.push(row);
    replaceRows(next);
  };

  const contents = entriesOf((r) => (CONTENT_KINDS as readonly string[]).includes(r.kind));
  const moveContent = (pos: number, dir: -1 | 1): void => {
    const j = pos + dir;
    if (j < 0 || j >= contents.length) return;
    const a = contents[pos]!.index;
    const b = contents[j]!.index;
    const next = [...rows];
    [next[a], next[b]] = [next[b]!, next[a]!];
    replaceRows(next);
  };

  const groupRow = rows.find((r): r is Extract<ClauseRow, { kind: "groupBy" }> => r.kind === "groupBy");
  const onRow = rows.find((r): r is Extract<ClauseRow, { kind: "on" }> => r.kind === "on");
  const atEntry = entriesOf((r) => r.kind === "at")[0];
  const colorRow = rows.find((r): r is Extract<ClauseRow, { kind: "color" }> => r.kind === "color");
  const styleEntries = entriesOf((r) => r.kind === "style");
  // Knob DECLS ride the row list as raw rows (they are header members, not
  // clauses — the save routes them through `setKnobs`). They get their own
  // section so they read as the view's parameters, not as an escape hatch.
  const knobEntries = entriesOf((r) => r.kind === "raw" && isKnobRowText(r.text));
  const rawEntries = entriesOf((r) => r.kind === "raw" && !isKnobRowText(r.text));

  // A loaded process view carries a `flow` clause — the builder keeps its editor
  // so it stays editable, but never creates one (process views are authored on
  // the process itself). A structure view has none.
  const flowEntry = entriesOf((r) => r.kind === "flow")[0] ?? null;
  // The structure representation (board when none of table/matrix/grid).
  const structEntry = entriesOf((r) => (STRUCTURE_KINDS as readonly string[]).includes(r.kind))[0] ?? null;
  const repChoice: RepChoice = (structEntry?.row.kind as RepChoice) ?? "board";
  const setRep = (choice: RepChoice): void => {
    // Exactly one representation — clearing a stray `flow` too.
    const next = rows.filter((r) => !(REP_KINDS as readonly string[]).includes(r.kind));
    if (choice !== "board") next.push(seedRow(choice));
    replaceRows(next);
  };

  return (
    <>
      {/* Name is rendered by the host (see BuilderPanel / DocViewEditorPanel) —
          the composer starts at the derived summary. */}
      {summary && (
        <p
          data-testid="view-summary"
          className="rounded-md bg-slate-800/40 px-2 py-1.5 text-[11px] leading-snug text-slate-400"
        >
          {summary}
        </p>
      )}

      {/* CONTENTS — the subset (the only ordered section). */}
      <Section title="Contents">
        {contents.map((e, pos) => (
          <div
            key={e.index}
            className="space-y-1 rounded-md border border-slate-800 p-1.5"
            onContextMenu={(ev) => menu.open(ev, rowMenu(e.index, rowText(e.row) ?? String(e.row.kind)))}
          >
            <div className="flex items-center gap-1">
              <span className="w-10 text-[11px] font-medium text-slate-400">
                {e.row.kind as ContentKind}
              </span>
              <span className="flex-1 truncate font-mono text-[10px] text-slate-600">
                {rowText(e.row) ?? "· pick a target"}
              </span>
              <IconButton
                size="xs"
                aria-label={`Move ${e.row.kind} ${pos + 1} up`}
                disabled={pos === 0}
                onClick={() => moveContent(pos, -1)}
              >
                <ArrowUp size={11} />
              </IconButton>
              <IconButton
                size="xs"
                aria-label={`Move ${e.row.kind} ${pos + 1} down`}
                disabled={pos === contents.length - 1}
                onClick={() => moveContent(pos, 1)}
              >
                <ArrowDown size={11} />
              </IconButton>
              <IconButton
                size="xs"
                aria-label={`Remove ${e.row.kind} ${pos + 1}`}
                onClick={() => removeRow(e.index)}
              >
                <X size={11} />
              </IconButton>
            </div>
            <RowEditor index={e.index} row={e.row} vocab={vocab} onChange={(r) => setRow(e.index, r)} />
          </div>
        ))}
        <div className="flex gap-3">
          {CONTENT_KINDS.map((k) => (
            <Button
              key={k}
              variant="ghost"
              size="sm"
              aria-label={`Add ${k} clause`}
              onClick={() => addRow(seedRow(k))}
            >
              <Plus size={11} /> {k}
            </Button>
          ))}
        </div>
      </Section>

      {/* GROUPING — group by aspect + narrow to a plane. */}
      <Section title="Grouping">
        <div className="flex items-center gap-2">
          <span className="w-12 shrink-0 text-[11px] text-slate-500">group</span>
          <select
            aria-label="Group by aspect or container"
            className="flex-1 rounded-sm border border-slate-700 bg-slate-800 px-1 py-0.5 text-xs text-slate-300"
            value={groupRow?.scope ? `in:${groupRow.scope}` : (groupRow?.key ?? "")}
            onChange={(e) => {
              const v = e.target.value;
              // `in:<path>` is the CONTAINMENT form — the container becomes a
              // frame around the items inside it (VIEWS §4).
              const row = v.startsWith("in:")
                ? { kind: "groupBy" as const, key: "", scope: v.slice(3) }
                : v
                  ? { kind: "groupBy" as const, key: v }
                  : null;
              setSingle("groupBy", row);
            }}
          >
            <option value="">— none —</option>
            <optgroup label="by aspect value">
              {vocab.aspectKeys.map((k) => (
                <option key={k} value={k}>
                  @@{k}
                </option>
              ))}
            </optgroup>
            <optgroup label="inside container">
              {/* A hand-written container selector (`in (A or B)`, `in domain`)
                  is not one of the pickable elements — list it so the control
                  shows what the view actually groups by instead of reading
                  "— none —" over a clause that is very much set. */}
              {groupRow?.scope && !vocab.elements.includes(groupRow.scope) ? (
                <option value={`in:${groupRow.scope}`}>in {groupRow.scope}</option>
              ) : null}
              {vocab.elements.map((p) => (
                <option key={p} value={`in:${p}`}>
                  in {p}
                </option>
              ))}
            </optgroup>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-12 shrink-0 text-[11px] text-slate-500">plane</span>
          {vocab.planes.length === 0 ? (
            <span className="text-[11px] italic text-slate-600">no planes declared</span>
          ) : (
            <PlaneSelect
              planes={vocab.planes}
              value={onRow?.plane || null}
              onChange={(p) => setSingle("on", p ? { kind: "on", plane: p } : null)}
              clearLabel="— primary —"
              aria-label="On plane"
              title="Project onto a plane's presence subset — primary = the whole structural model"
            />
          )}
        </div>
        {/* `at` — the point on the lifecycle timeline the view reads the model
            at. Single-slot like group/plane: a view carries at most one. It
            paints, never selects (spec §9.1), so it belongs beside them. */}
        <div className="flex items-start gap-2">
          <span className="w-12 shrink-0 pt-0.5 text-[11px] text-slate-500">time</span>
          {atEntry ? (
            <div className="min-w-0 flex-1 space-y-1">
              <RowEditor
                index={atEntry.index}
                row={atEntry.row}
                vocab={vocab}
                onChange={(r) => setRow(atEntry.index, r)}
              />
              <Button
                variant="ghost"
                size="xs"
                aria-label="Remove the at clause"
                onClick={() => setSingle("at", null)}
              >
                <X size={10} /> whole roadmap
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="xs"
              aria-label="Add an at clause"
              onClick={() => setSingle("at", seedRow("at"))}
            >
              <Plus size={10} /> read at a point…
            </Button>
          )}
        </div>
      </Section>

      {/* STYLE — colour by aspect (the common case) + per-selector rules. */}
      <Section title="Style">
        <div className="flex items-center gap-2">
          <span className="w-12 shrink-0 text-[11px] text-slate-500">colour</span>
          <ColorBySelect
            aspectKeys={vocab.aspectKeys}
            value={colorRow?.key ?? null}
            onChange={(k) => setSingle("color", k ? { kind: "color", key: k } : null)}
            noneLabel="by type"
            aria-label="Colour by aspect"
            title="Colour every module by an aspect key"
          />
        </div>
        {styleEntries.map((e) => (
          <div
            key={e.index}
            className="space-y-1 rounded-md border border-slate-800 p-1.5"
            onContextMenu={(ev) => menu.open(ev, rowMenu(e.index, rowText(e.row) ?? "style"))}
          >
            <div className="flex items-center gap-1">
              <span className="flex-1 truncate font-mono text-[10px] text-slate-600">
                {rowText(e.row) ?? "· style rule"}
              </span>
              <IconButton
                size="xs"
                aria-label={`Remove style rule ${e.index + 1}`}
                onClick={() => removeRow(e.index)}
              >
                <X size={11} />
              </IconButton>
            </div>
            <RowEditor index={e.index} row={e.row} vocab={vocab} onChange={(r) => setRow(e.index, r)} />
          </div>
        ))}
        <Button
          variant="ghost"
          size="sm"
          aria-label="Add style rule"
          onClick={() => addRow(seedRow("style"))}
        >
          <Plus size={11} /> style rule
        </Button>
      </Section>

      {/* REPRESENTATION — a structure view drawn as board / table / matrix /
          grid (these reshape the same `show` set). No Structure/Process switch:
          process views are authored on the process itself. A loaded process
          view still shows its flow editor so it stays editable. */}
      <Section title="Representation">
        {flowEntry ? (
          <div className="rounded-md border border-slate-800 p-1.5">
            <RowEditor
              index={flowEntry.index}
              row={flowEntry.row}
              vocab={vocab}
              onChange={(r) => setRow(flowEntry.index, r)}
            />
          </div>
        ) : (
          <>
            <div className="flex gap-1" role="group" aria-label="Representation">
              {STRUCTURE_CHOICES.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={`Representation ${REP_LABELS[c]}`}
                  aria-pressed={repChoice === c}
                  onClick={() => setRep(c)}
                  className={`flex-1 rounded-md border px-1 py-1 text-[11px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 ${
                    repChoice === c
                      ? "border-transparent bg-violet-500/80 font-medium text-white"
                      : "border-slate-700 bg-slate-800 text-slate-400 hover:bg-white/10"
                  }`}
                >
                  {REP_LABELS[c]}
                </button>
              ))}
            </div>
            {structEntry && (
              <div className="rounded-md border border-slate-800 p-1.5">
                <RowEditor
                  index={structEntry.index}
                  row={structEntry.row}
                  vocab={vocab}
                  onChange={(r) => setRow(structEntry.index, r)}
                />
              </div>
            )}
          </>
        )}
      </Section>

      {/* KNOBS — the view's parameters (header decls, written before the first
          clause). Declaration text is typed: the shapes (`knob n: 2 { min }`,
          `knob module target`, `knob z from @@key`, the block form) do not
          reduce to one picker, and a wrong one is caught by the live compile. */}
      {knobEntries.length > 0 || knobsOpen ? (
        <Section title="Knobs">
          {knobEntries.map((e) => (
            <div key={e.index} className="flex items-center gap-1">
              <div className="flex-1">
                <input
                  aria-label={`Knob ${e.index + 1} declaration`}
                  className="w-full rounded-sm border border-slate-700 bg-slate-800 px-2 py-1 font-mono text-xs text-slate-200 focus:border-slate-500 focus:outline-hidden"
                  placeholder="knob depth: 2 { min: 1; max: 5 }"
                  value={(e.row as { text: string }).text}
                  onChange={(ev) => setRow(e.index, { kind: "raw", text: ev.target.value })}
                />
              </div>
              <IconButton size="xs" aria-label={`Remove knob ${e.index + 1}`} onClick={() => removeRow(e.index)}>
                <X size={11} />
              </IconButton>
            </div>
          ))}
          <Button
            variant="ghost"
            size="sm"
            aria-label="Add knob"
            onClick={() => addRow({ kind: "raw", text: "knob " })}
          >
            <Plus size={11} /> knob
          </Button>
        </Section>
      ) : (
        <Button
          variant="ghost"
          size="xs"
          className="justify-start border-t border-slate-800"
          aria-label="Show knobs"
          onClick={() => setKnobsOpen(true)}
        >
          ▸ Knobs
        </Button>
      )}

      {/* ADVANCED — the raw-clause escape hatch for anything sections omit. */}
      {rawEntries.length > 0 || rawOpen ? (
        <Section title="Advanced (raw clauses)">
          {rawEntries.map((e) => (
            <div
              key={e.index}
              className="flex items-center gap-1"
              onContextMenu={(ev) => menu.open(ev, rowMenu(e.index, rowText(e.row) ?? "raw"))}
            >
              <div className="flex-1">
                <RowEditor index={e.index} row={e.row} vocab={vocab} onChange={(r) => setRow(e.index, r)} />
              </div>
              <IconButton
                size="xs"
                aria-label={`Remove raw clause ${e.index + 1}`}
                onClick={() => removeRow(e.index)}
              >
                <X size={11} />
              </IconButton>
            </div>
          ))}
          <Button
            variant="ghost"
            size="sm"
            aria-label="Add raw clause"
            onClick={() => addRow(seedRow("raw"))}
          >
            <Plus size={11} /> raw clause
          </Button>
        </Section>
      ) : (
        <Button
          variant="ghost"
          size="xs"
          className="justify-start border-t border-slate-800"
          onClick={() => setRawOpen(true)}
        >
          ▸ Advanced (raw clause)
        </Button>
      )}

      {errors.length > 0 && (
        <div className="text-[11px] text-red-400/90 space-y-0.5" role="alert">
          {errors.map((e, i) => (
            <div key={i}>{e}</div>
          ))}
        </div>
      )}
      {menu.node}
    </>
  );
}

function BuilderPanel({
  drafts,
  canSave,
  onClose,
}: {
  drafts: ReturnType<typeof useViewDrafts>;
  canSave: boolean;
  onClose: () => void;
}): ReactElement {
  const [note, setNote] = useState<string | null>(null);
  const vocab = useSelectorVocabulary();
  const d = drafts.editing;

  return (
    <>
      {/* Name: LOCKED when modifying an existing view (you're reshaping THAT
          view, not renaming it); an editable field only when creating a new one.
          Both are about what a SAVE would be called, so an ephemeral builder
          shows neither — naming something that is never written is busywork. */}
      {!canSave ? null : drafts.isModifying ? (
        <div
          data-testid="view-name-locked"
          className="w-full rounded-sm border border-slate-700 bg-slate-800/60 px-2 py-1 text-sm text-slate-300"
        >
          <span className="text-[10px] uppercase tracking-wide text-slate-500">Modifying </span>
          {d.name}
        </div>
      ) : (
        <input
          aria-label="View name"
          className="w-full bg-slate-800 border border-slate-700 rounded-sm px-2 py-1 text-sm text-slate-200 focus:border-slate-500 focus:outline-hidden"
          placeholder="View name"
          value={d.name}
          onChange={(e) => drafts.setName(e.target.value)}
        />
      )}
      {canSave && drafts.nameCollision && (
        <div className="text-[11px] text-amber-400/90">a model view already has this name — saving is blocked</div>
      )}

      {/* The shared composer body (concern sections), backed by the draft store:
          every row change funnels through replaceRows (which normalises `show *`). */}
      <ViewClauseComposer
        rows={d.rows}
        onChangeRows={drafts.replaceRows}
        errors={drafts.errors}
        vocab={vocab}
      />

      {note && (
        <div className="text-[11px] text-amber-400/90" role="status">
          {note}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-1 border-t border-slate-800">
        {/* No preview chrome: edits reconfigure the board live as you type,
            always on. Reset reverts to the saved view (or clears a new config);
            Save writes it to the model. */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            drafts.reset();
            setNote(null);
          }}
        >
          Reset
        </Button>
        {canSave && (
        <Button
          variant="primary"
          size="sm"
          disabled={drafts.errors.length > 0 || drafts.nameCollision || !d.name.trim()}
          onClick={() => {
            const r = drafts.saveToModel();
            if (r.status === "applied") {
              setNote(null);
              onClose();
            } else if (r.status === "refused") setNote(r.message);
            else if (r.status === "stale") setNote("compile catching up — try again");
            else setNote("no .arch file to save into");
          }}
        >
          {drafts.isModifying ? "Update view" : "Save as view"}
        </Button>
        )}
      </div>
    </>
  );
}
